import * as React from 'react';
import { cva } from 'class-variance-authority';
import { Dialog as DialogPrimitive } from 'radix-ui';

import { cn } from '@/lib/utils';

const modalHeaderVariants = cva(
  'relative z-[1] flex flex-none items-start justify-between gap-3 border-b border-modal-glass-border px-6 py-6 [&_h1]:!m-0 [&_h2]:!m-0 [&_h3]:!m-0 [&_h4]:!m-0 [&_h1]:!text-neutral-25 [&_h2]:!text-neutral-25 [&_h3]:!text-neutral-25 [&_h4]:!text-neutral-25'
);

const modalBodyVariants = cva(
  'relative z-[1] flex-auto overflow-y-auto px-6 py-6 [&_.cloze]:font-bold [&_.cloze]:text-status-success [&_.explainer]:border-b [&_.explainer]:border-dashed [&_.explainer]:border-white/50 [&_.card-wrapper]:flow-root [&_.card-wrapper_.image-display]:float-right [&_.card-wrapper_.image-display]:max-w-1/2 [&_.card-wrapper_.image-display]:bg-transparent [&_.card-wrapper_.image-display]:pb-6 [&_.card-wrapper_.image-display]:pl-6 [&_.card-wrapper_.image-display.is-single]:!pr-0 [&_.card-wrapper_.image-display.is-single_.image-display_row]:!hidden [&_.card-wrapper_.image-display_.custom-image-container]:mb-0 [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.caption]:leading-6 [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.caption]:text-[calc(1em-0.25rem)] [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.caption_*]:text-[1em] [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.credit]:leading-6 [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.credit]:text-[calc(1em-0.25rem)] [&_.card-wrapper_.image-display_.custom-image-container_.caption-credit_.credit_*]:text-[1em] [&_.card-wrapper_.image-display_.custom-image-container_img]:h-auto [&_.card-wrapper_.image-display_.custom-image-container_img]:w-full [&_.card-wrapper_.image-display_.custom-image-container_img]:max-w-full max-mobile:[&_.card-wrapper_.image-display]:w-full max-mobile:[&_.card-wrapper_.image-display]:max-w-full max-mobile:[&_.card-wrapper_.image-display]:float-none max-mobile:[&_.card-wrapper_.image-display]:pl-0 max-mobile:[&_.card-wrapper_.image-display]:pb-6'
);

const modalFooterVariants = cva(
  'relative z-[1] flex flex-none items-center justify-between gap-3 border-t border-modal-glass-border px-6 py-6 [&>*:only-child]:ml-auto min-md:[&_button]:min-w-20'
);

const ModalHeader = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="modal-header"
    className={cn(modalHeaderVariants(), className)}
    {...props}
  />
));

ModalHeader.displayName = 'ModalHeader';

const ModalBody = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-slot="modal-body"
      className={cn(modalBodyVariants(), className)}
      {...props}
    />
  )
);

ModalBody.displayName = 'ModalBody';

const ModalFooter = React.forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="modal-footer"
    className={cn(modalFooterVariants(), className)}
    {...props}
  />
));

ModalFooter.displayName = 'ModalFooter';

interface ModalProps {
  open: boolean;
  onClose: (isOpen: boolean) => void;
  name: string;
  title?: string;
  description?: string;
  ariaLabel?: string;
  ariaLabelledby?: string;
  ariaDescribedby?: string;
  size?: 'sm' | 'default' | 'lg' | 'xl';
  className?: string;
  blurs?: boolean;
  blurType?: 'success' | 'error' | 'accent';
  children: React.ReactNode;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  unsavedChanges?: boolean;
  handleUnsavedChanges?: () => void;
  /** Prevent sensitive dialog contents from entering the companion snapshot. */
  excludeFromChatSnapshot?: boolean;
  /**
   * Where focus goes once the modal closes. The modal has no trigger of its
   * own, so without this focus falls back to the page.
   */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
}

const formatModalNameLabel = (value: string) =>
  value.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();

const Modal: React.FC<ModalProps> = ({
  open,
  onClose,
  children,
  name,
  title,
  description,
  ariaLabel,
  ariaLabelledby,
  ariaDescribedby,
  size = 'default',
  className,
  blurs,
  blurType = 'success',
  disableBackdropClick,
  disableEscapeKeyDown,
  unsavedChanges,
  handleUnsavedChanges,
  excludeFromChatSnapshot = false,
  returnFocusRef,
}) => {
  const closeModal = React.useCallback(() => {
    onClose(false);
  }, [onClose]);

  const generatedId = React.useId();
  const generatedTitleId = `modal-title-${generatedId.replace(/:/g, '')}`;
  const generatedDescriptionId = `modal-description-${generatedId.replace(/:/g, '')}`;

  const resolvedLabelledBy =
    ariaLabelledby ?? (title ? generatedTitleId : undefined);

  const resolvedDescribedBy =
    ariaDescribedby ?? (description ? generatedDescriptionId : undefined);

  const resolvedAriaLabel =
    ariaLabel ??
    (!resolvedLabelledBy ? formatModalNameLabel(name) || 'Dialog' : undefined);

  const sizeClasses = {
    sm: 'w-[24rem]',
    default: 'w-[32rem]',
    lg: 'w-[48rem]',
    xl: 'w-[64rem]',
  }[size];

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          closeModal();
        }
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'modal-overlay fixed inset-0 z-[2600] bg-black/55 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
            disableBackdropClick && 'backdrop-blur-[10px]',
            `modal-overlay--${name}`
          )}
        />
        <DialogPrimitive.Content
          data-slot="modal"
          data-chat-snapshot={excludeFromChatSnapshot ? 'exclude' : undefined}
          aria-label={resolvedAriaLabel}
          aria-labelledby={resolvedLabelledBy}
          aria-describedby={resolvedDescribedBy}
          style={{ willChange: 'opacity' }}
          className={cn(
            'fixed left-1/2 top-1/2 z-[2601] flex max-h-[calc(100vh-6rem)] max-w-[calc(100vw-3rem)] -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-xl border border-modal-glass-border bg-[rgba(0,0,0,0.6)] text-modal-fg shadow-popover outline-none backdrop-blur-[20px] data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 dark:bg-[rgba(255,255,255,0.1)] [--text-primary:var(--color-modal-fg)] [--text-secondary:var(--color-neutral-200)] [--text-inverse:var(--color-modal-inverse)] [--text-disabled:var(--color-neutral-300)] [--surface-canvas:var(--color-modal-field)] [--surface-highlight:var(--color-modal-field)] [--surface-tint:var(--color-modal-field)] [--surface-tint-hover:var(--color-modal-field-hover)] [--surface-tint-inverse:var(--color-modal-field)] [--surface-modal:var(--color-modal-surface)] [--surface-modal-glass-border:var(--color-modal-border)] [--border-subtle:var(--color-modal-border)] [--color-fg-primary:var(--color-modal-fg)] [--color-fg-secondary:var(--color-neutral-200)] [--color-fg-inverse:var(--color-modal-inverse)] [--color-surface-canvas:var(--color-modal-field)] [--color-surface-muted:var(--color-modal-muted)] [--color-surface-input:var(--color-modal-field)] [--color-border-subtle:var(--color-modal-border)] [--input-bg:var(--color-modal-field)] [--input-border-color:transparent] [--input-fg:var(--color-modal-fg)] [--input-placeholder:var(--color-modal-fg)] [--input-focus-border:var(--color-modal-fg)] [--input-focus-ring:var(--color-modal-fg)] [--or-divider-color:var(--color-neutral-0-10)] [&_h1]:text-modal-heading [&_h2]:text-modal-heading [&_h3]:text-modal-heading [&_h4]:text-modal-heading [&_h5]:text-modal-heading [&_h6]:text-modal-heading [&_strong]:text-modal-heading [&_.or]:before:!bg-[var(--color-neutral-0-10)] [&_.or]:after:!bg-[var(--color-neutral-0-10)]',
            sizeClasses,
            `modal--${name}`,
            className
          )}
          onCloseAutoFocus={(event) => {
            const target = returnFocusRef?.current;
            if (!target) return;
            event.preventDefault();
            target.focus();
          }}
          onEscapeKeyDown={(event) => {
            if (disableEscapeKeyDown) {
              event.preventDefault();
            }
          }}
          onPointerDownOutside={(event) => {
            if (unsavedChanges && handleUnsavedChanges) {
              event.preventDefault();
              handleUnsavedChanges();
              return;
            }
            if (disableBackdropClick) {
              event.preventDefault();
            }
          }}
          onInteractOutside={(event) => {
            if (disableBackdropClick) {
              event.preventDefault();
            }
          }}
        >
          {title ? (
            <DialogPrimitive.Title id={generatedTitleId} className="sr-only">
              {title}
            </DialogPrimitive.Title>
          ) : null}
          {description ? (
            <DialogPrimitive.Description
              id={generatedDescriptionId}
              className="sr-only"
            >
              {description}
            </DialogPrimitive.Description>
          ) : null}
          {children}
          {blurs && (
            <>
              {blurType === 'success' && (
                <>
                  <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[69px] w-[104px] translate-y-1/2 bg-[#004975] opacity-70 blur-[47px]" />
                  <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[69px] w-[104px] translate-y-full bg-[#5fe19b] opacity-70 blur-[47px]" />
                </>
              )}
              {blurType === 'error' && (
                <>
                  <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[69px] w-[104px] translate-y-1/2 bg-status-danger opacity-70 blur-[47px]" />
                  <div className="pointer-events-none absolute bottom-0 right-0 z-0 h-[69px] w-[104px] translate-y-full bg-status-danger opacity-70 blur-[47px]" />
                </>
              )}
              {blurType === 'accent' && (
                <>
                  <div className="pointer-events-none absolute right-[-0.5rem] top-[-0.5rem] z-0 h-1/2 w-16 bg-[#7687e3] blur-[60px]" />
                  <div className="pointer-events-none absolute right-2 top-0 z-0 h-[30%] w-[30%] -translate-y-1/2 bg-[#df8f8f] blur-[60px]" />
                </>
              )}
            </>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export { Modal, ModalHeader, ModalBody, ModalFooter };
